import {
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Avatar, usePicture } from '../Avatar';
import {
  ArrowLeft,
  Bell,
  BellOff,
  MoreVertical,
  Phone,
  Search,
  Settings,
  ShieldCheck,
  Video,
  Heart,
  Flag,
  Ban,
  Timer,
} from 'lucide-react';
import { durationLabel } from '../../lib/disappearing';
import { useCall } from '../../hooks';
import { avatarPairIndex } from '../../theme';
import type { ChatConversation, ChatParticipant } from './ConversationDialogs';
import { conversationName } from './messageUtils';
import { SecurityCodeDialog } from './SecurityCodeDialog';
import { muteUntilLabel } from './ChatActionsSheet';
import { memberLabelsAtom } from '../../store';
import { useAtomValue } from 'jotai';
import { RelationNameDialog } from '../family/RelationNameDialog';
import { PeopleSheet, ProfilePhotoViewer } from './PeopleSheet';
import { useT } from '../../i18n';

/**
 * One of the small faces under a chat's name (D279): the picture once it
 * opens on this device, the person's colour until then (D294).
 */
function HeaderFace({
  person,
  familyId,
  first,
}: {
  person: ChatParticipant;
  familyId: string;
  first: boolean;
}) {
  const photo = usePicture(person.avatarUrl, {
    ownerId: person.id,
    familyId,
  });
  return photo ? (
    <Avatar
      src={photo}
      name={person.displayName}
      w="20px"
      h="20px"
      ml={first ? 0 : '-5px'}
      showBorder={false}
      boxShadow="0 0 0 2px var(--chakra-colors-canvas)"
      aria-hidden="true"
      data-testid="header-face-photo"
    />
  ) : (
    <Box
      w="20px"
      h="20px"
      borderRadius="full"
      bg={`avatar.${avatarPairIndex(person.displayName)}.bg`}
      ml={first ? 0 : '-5px'}
      boxShadow="0 0 0 2px var(--chakra-colors-canvas)"
      flexShrink={0}
      aria-hidden="true"
    />
  );
}

function typingLabel(names: string[], t: ReturnType<typeof useT>): string {
  if (names.length === 1)
    return t('chat.header.typingOne', { name: names[0] ?? '' });
  if (names.length === 2)
    return t('chat.header.typingTwo', {
      first: names[0] ?? '',
      second: names[1] ?? '',
    });
  return t('chat.header.typingMany');
}

interface ChatHeaderProps {
  conversation: ChatConversation;
  currentUserId: string | undefined;
  isMuted: boolean;
  /** When a timed mute ends (D233); null for Always. */
  mutedUntil?: string | null;
  /** Group info: any member of a group; the family group, its admins (D234). */
  canOpenGroupInfo?: boolean;
  /** Names of people typing right now (D24). */
  typingNames: string[];
  onBack: () => void;
  onSearch: () => void;
  /** Opens the mute sheet (8 hours, 1 week, Always; D233). */
  onMute: () => void;
  onUnmute: () => void;
  onOpenSettings: () => void;
  /** Report the other person in a direct chat (M58, D207). */
  onReport?: () => void;
  /** Block the other person in a direct chat (D208). */
  onBlock?: () => void;
  /** Opens the disappearing messages sheet, where I may change it (D238). */
  onDisappearing?: () => void;
}

export function ChatHeader({
  conversation,
  currentUserId,
  isMuted,
  mutedUntil = null,
  canOpenGroupInfo = false,
  typingNames,
  onBack,
  onSearch,
  onMute,
  onUnmute,
  onOpenSettings,
  onReport,
  onBlock,
  onDisappearing,
}: ChatHeaderProps) {
  const { startCall } = useCall();
  const t = useT();
  const memberLabels = useAtomValue(memberLabelsAtom);
  const name = conversationName(conversation, currentUserId, memberLabels);
  const muteLabel = isMuted
    ? t('chat.header.unmute')
    : t('chat.header.muteEllipsis');
  const hasSettings = canOpenGroupInfo;

  const others = conversation.participants.filter(
    (p) => p.id !== currentUserId
  );
  const securityCodes = useDisclosure();
  const relation = useDisclosure();
  // The faces under the name (D279): a group's people, or the other person's picture.
  const people = useDisclosure();
  const me = conversation.participants.find((p) => p.id === currentUserId);
  // Security codes are per person, so they're offered in direct chats (D63).
  const other = conversation.type === 'direct' ? (others[0] ?? null) : null;
  const title = conversation.isDefault ? t('chat.header.everyone') : name;
  const tintButton = {
    variant: 'ghost',
    bg: 'accent.tint',
    color: 'accent',
    _hover: { bg: 'brand.100' },
    _dark: { _hover: { bg: 'brand.700' } },
  } as const;

  return (
    <Box
      px={{ base: 2, md: 4 }}
      // On phones the app header is hidden while a chat is open, so this bar
      // sits directly under the status bar / notch.
      pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 3 }}
      pb={2}
      flexShrink={0}
    >
      <HStack spacing={1} minW={0} minH="56px">
        <IconButton
          aria-label={t('chat.header.back')}
          icon={<ArrowLeft size={24} />}
          variant="ghost"
          colorScheme="gray"
          display={{ base: 'inline-flex', md: 'none' }}
          onClick={onBack}
        />
        <Box flex="1" minW={0} pl={{ base: 0, md: 1 }}>
          <Text
            fontFamily="heading"
            fontWeight={800}
            fontSize="22px"
            letterSpacing="-0.02em"
            lineHeight={1.1}
            noOfLines={1}
          >
            {title}
            {conversation.disappearingSeconds ? (
              // Disappearing messages are on (D238).
              <Box
                as="span"
                display="inline-flex"
                verticalAlign="middle"
                ml="6px"
                color="muted"
                data-testid="disappearing-timer"
                role="img"
                aria-label={t('chat.disappearing.headerOn', {
                  time: durationLabel(conversation.disappearingSeconds),
                })}
              >
                <Timer size={18} aria-hidden="true" />
              </Box>
            ) : null}
          </Text>
          <HStack spacing={0} mt="2px" minW={0}>
            {/* A few faces, then who's typing or how many people. Their
                pictures where they have one; a tap shows them (D279). */}
            <HStack
              as="button"
              type="button"
              spacing={0}
              flexShrink={0}
              borderRadius="full"
              aria-label={
                other
                  ? t('chat.people.seePhoto', {
                      name: memberLabels[other.id] ?? other.displayName,
                    })
                  : t('chat.people.seeWho')
              }
              onClick={people.onOpen}
              data-testid="header-faces"
            >
              {others.slice(0, 4).map((person, i) => (
                <HeaderFace
                  key={person.id}
                  person={person}
                  familyId={conversation.familyId}
                  first={i === 0}
                />
              ))}
            </HStack>
            <Text
              fontSize="13px"
              color={typingNames.length > 0 ? 'accent' : 'muted'}
              pl="6px"
              noOfLines={1}
            >
              {typingNames.length > 0
                ? typingLabel(typingNames, t)
                : conversation.type === 'group'
                  ? t('chat.header.people', {
                      n: conversation.participants.length,
                    })
                  : others[0]?.handle
                    ? `@${others[0].handle}`
                    : ''}
            </Text>
          </HStack>
        </Box>

        <HStack spacing={1} flexShrink={0}>
          <IconButton
            aria-label={t('chat.header.search')}
            icon={<Search size={20} />}
            variant="ghost"
            colorScheme="gray"
            display={{ base: 'none', md: 'inline-flex' }}
            onClick={onSearch}
          />
          <IconButton
            aria-label={t('chat.header.voiceCall')}
            icon={<Phone size={20} />}
            {...tintButton}
            onClick={() => void startCall(conversation.id, 'voice')}
          />
          <IconButton
            aria-label={t('chat.header.videoCall')}
            icon={<Video size={22} />}
            {...tintButton}
            onClick={() => void startCall(conversation.id, 'video')}
          />
          <Menu placement="bottom-end" isLazy>
            <MenuButton
              as={IconButton}
              aria-label={t('chat.header.more')}
              icon={<MoreVertical size={20} />}
              variant="ghost"
              colorScheme="gray"
            />
            <MenuList>
              <MenuItem
                icon={<Search size={16} />}
                onClick={onSearch}
                display={{ base: 'flex', md: 'none' }}
              >
                {t('chat.header.search')}
              </MenuItem>
              <MenuItem
                icon={isMuted ? <Bell size={16} /> : <BellOff size={16} />}
                onClick={isMuted ? onUnmute : onMute}
              >
                <Box>
                  <Text>{muteLabel}</Text>
                  {isMuted && mutedUntil && (
                    <Text fontSize="13px" color="muted">
                      {t('chat.header.mutedUntil', {
                        time: muteUntilLabel(mutedUntil),
                      })}
                    </Text>
                  )}
                </Box>
              </MenuItem>
              {hasSettings && (
                <MenuItem
                  icon={<Settings size={16} />}
                  onClick={onOpenSettings}
                >
                  {t('chat.header.groupSettings')}
                </MenuItem>
              )}
              {onDisappearing && (
                <MenuItem icon={<Timer size={16} />} onClick={onDisappearing}>
                  <Box>
                    <Text>{t('chat.disappearing.title')}</Text>
                    <Text fontSize="13px" color="muted">
                      {durationLabel(conversation.disappearingSeconds)}
                    </Text>
                  </Box>
                </MenuItem>
              )}
              {other && (
                <MenuItem icon={<Heart size={16} />} onClick={relation.onOpen}>
                  {t('chat.header.whatDoYouCall', {
                    name: other.displayName.split(' ')[0] ?? '',
                  })}
                </MenuItem>
              )}
              {other && me && (
                <MenuItem
                  icon={<ShieldCheck size={16} />}
                  onClick={securityCodes.onOpen}
                >
                  {t('chat.header.securityCodes')}
                </MenuItem>
              )}
              {other && onReport && (
                <MenuItem icon={<Flag size={16} />} onClick={onReport}>
                  {t('chat.header.report')}
                </MenuItem>
              )}
              {other && onBlock && (
                <MenuItem
                  icon={<Ban size={16} />}
                  color="danger"
                  onClick={onBlock}
                >
                  {t('chat.header.block', {
                    name: other.displayName.split(' ')[0] ?? '',
                  })}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </HStack>
      </HStack>
      {other && (
        <RelationNameDialog
          person={{ id: other.id, name: other.displayName }}
          isOpen={relation.isOpen}
          onClose={relation.onClose}
        />
      )}
      {other ? (
        <ProfilePhotoViewer
          person={
            people.isOpen
              ? {
                  ...other,
                  displayName: memberLabels[other.id] ?? other.displayName,
                }
              : null
          }
          familyId={conversation.familyId}
          onClose={people.onClose}
        />
      ) : (
        <PeopleSheet
          isOpen={people.isOpen}
          participants={conversation.participants}
          currentUserId={currentUserId}
          familyId={conversation.familyId}
          onClose={people.onClose}
        />
      )}
      {other && me && (
        <SecurityCodeDialog
          me={{ id: me.id, name: me.displayName }}
          them={{ id: other.id, name: other.displayName }}
          isOpen={securityCodes.isOpen}
          onClose={securityCodes.onClose}
        />
      )}
    </Box>
  );
}
